import {
  Action,
  FlowBlock,
  Condition,
  ConditionalStatement,
} from '@staffino/model';
import accept from './block-visitor';

function isValidAnswerCondition(config: any = {}) {
  if (!config.blockId) {
    return false;
  }
  // check choice question
  if (
    ['contains', '!contains', 'equals', '='].includes(
      config.choiceIdsOperator,
    ) &&
    Array.isArray(config.choiceIds) &&
    config.choiceIds.length > 0
  ) {
    return true;
  }
  if (['empty', '!empty'].includes(config.choiceIdsOperator)) {
    return true;
  }
  // check rating question
  if (
    config.operator === 'contains' &&
    Array.isArray(config.value) &&
    config.value.length > 0
  ) {
    return true;
  }
  if (config.operator === 'equals' && (!!config.value || config.value === 0)) {
    return true;
  }
  if (['empty', '!empty'].includes(config.operator)) {
    return true;
  }

  // open question
  if (['empty', '!empty'].includes(config.commentOperator)) {
    return true;
  }
  if (
    ['>', '<'].includes(config.commentOperator) &&
    typeof config.commentLength === 'number' &&
    Number.isFinite(config.commentLength)
  ) {
    return true;
  }
  if (
    ['contains', '!contains'].includes(config.commentOperator) &&
    typeof config.comment === 'string' &&
    config.comment.length > 0
  ) {
    return true;
  }

  return false;
}

function isValidInputCondition(config: any = {}) {
  if (!config.blockId) {
    return false;
  }
  if (['empty', '!empty'].includes(config.operator)) {
    return true;
  }
  if (
    ['>', '<'].includes(config.operator) &&
    typeof config.length === 'number' &&
    Number.isFinite(config.length)
  ) {
    return true;
  }
  if (
    (['>', '<'].includes(config.operator) && config.value) ||
    (config.value === 0 && ['date', 'time', 'number'].includes(config.format))
  ) {
    return true;
  }
  if (
    ['contains', '!contains'].includes(config.operator) &&
    typeof config.value === 'string' &&
    config.value.length > 0
  ) {
    return true;
  }

  return false;
}

function isValidContainsCondition(config: any = {}) {
  if (
    ['contains', '!contains'].includes(config.operator) &&
    config.value?.length > 0
  ) {
    return true;
  }
  return false;
}

function isValidAttributeCondition(config: any = {}) {
  if (
    ['customer', 'interaction'].includes(config.kind) &&
    ['contains', '!contains'].includes(config.operator) &&
    config.attribute &&
    config.value?.length > 0
  ) {
    return true;
  }
  return false;
}

function isValidTargetCondition(config: any = {}) {
  if (['empty', '!empty'].includes(config.operator)) {
    return true;
  }
  return false;
}

function isValidParameterCondition(config: any = {}) {
  return !!config.name && !!config.value;
}

function isValidStructureCondition(config: any = {}) {
  if (
    ['contains', '!contains'].includes(config.operator) &&
    config.value?.length > 0
  ) {
    return true;
  }
  return false;
}

function isValidLegacyStructureCondition(config: any = {}) {
  if (
    ['contains', '!contains'].includes(config.operator) &&
    config.value?.length > 0
  ) {
    return true;
  }
  return false;
}

function isValidCondition(condition: Condition) {
  const [first, ...rest] = condition ?? [];
  if (first === '||' || first === '&&') {
    return rest.every(isValidCondition);
  }
  const config = rest[0];
  const action: string = first as string;
  switch (action) {
    case 'Flow.Answer':
    case 'Parameter':
      // legacy conditions
      return true;

    case 'answer':
      return isValidAnswerCondition(config);

    case 'input':
    case 'legal':
      return isValidInputCondition(config);

    case 'interaction.source':
      return isValidContainsCondition(config);

    case 'interaction.reason':
      return isValidContainsCondition(config);

    case 'customer.attribute':
    case 'interaction.attribute':
      return isValidAttributeCondition(config);
    case 'target':
      return isValidTargetCondition(config);
    case 'parameter':
      return isValidParameterCondition(config);
    case 'structure':
      return isValidStructureCondition(config);
    case 'legacyStructure':
      return isValidLegacyStructureCondition(config);

    default:
      return false;
  }
}

function isValidAction(action: Action) {
  const [name, config = {}] = action ?? [];
  switch (name) {
    // legacy actions, do not verify
    case 'Flow.SetNextPage':
    case 'Flow.Advance':
    case 'Flow.Restart':
    case 'Flow.Finish':
    case 'Block.Show':
    case 'Block.Hide':
      return true;

    // these need to have block id
    case 'block.show':
    case 'block.hide':
    case 'page.show':
    case 'page.hide':
    case 'makeRequired':
    case 'makeOptional':
      return !!config.blockId;

    case 'addLabel':
      return !!config.label;
    case 'customer.setAttribute':
    case 'interaction.setAttribute':
      return !!config.attribute && !!config.value;

    case 'setQuestionAnswer':
    case 'setElementAnswer':
      return !!config.blockId && !!config.value;

    default:
      return false;
  }
}

function isValidStatement(statement: ConditionalStatement): boolean {
  const validCondition = isValidCondition(statement.if);
  const validActions = statement.actions?.every(isValidAction);
  if (!validCondition) {
    console.debug('[EVAL] Invalid condition', statement.if);
  }
  if (!validActions) {
    console.debug('[EVAL] Invalid actions', statement.actions);
  }
  return validCondition && validActions;
}

function hasValidStatements(block: FlowBlock): boolean {
  if (
    !('conditions' in block) ||
    !block.conditions ||
    typeof block.conditions !== 'object' ||
    typeof block.conditions.length !== 'number' ||
    block.conditions.length === 0
  ) {
    return false;
  }

  return block.conditions.every(isValidStatement);
}

export default function extractValidConditionStatements(
  page: FlowBlock,
): ConditionalStatement[] {
  const result = [];

  const visitor = {
    enter(block: FlowBlock) {
      if (hasValidStatements(block) && 'conditions' in block) {
        result.push(...(block.conditions ?? []));
      }
    },
  };
  accept(page, visitor);

  return result;
}
